import { useEffect, useMemo, useState, useRef } from "react"; 
import { useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import useAuthStore from "../../../store/authStore";
import settingsIcon from "../../../assets/images/SBA/settings_icons.png";
import excelIcon from "../../../assets/images/SBA/excel_icon.png";
import pdfIcon from "../../../assets/images/SBA/pdf_icon.png";
import downloadIcon from "../../../assets/images/SBA/img_Download.png";
import newIcon from "../../../assets/images/SBA/New-icon.png";
import { toast } from "react-toastify";
import dropDownImg from "../../../assets/images/dropdown.svg";

import {
  getInvestorsByContact,
  getEntitiesByInvestor,
  getCategoryByEntity,
  getDocumentsByEntity,
  downloadDocumentBlob,
  downloadDocumentsZipBlob,
  openBlobInNewTab,
  saveBlob,
  getDocumentsCustomize,
  saveGridCustomize,
} from "../services/assetDocumentsService";
import "../styles/assetStatements.css";
import { exportToExcel } from "../../../shared/utils/exportToExcel";
import { AESClientWebsiteDrawer } from "../../../shared/utils/aesClientWebsiteDrawer";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import ToolbarSelect from "./ToolbarSelect";

ModuleRegistry.registerModules([AllCommunityModule]);

const get = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal];
const getDocTypeId = (x) =>
  x?.documentTypeID ?? x?.documentTypeId ?? x?.DocumentTypeID ?? x?.DocumentTypeId;

const DEFAULT_CUSTOMIZE_LABELS = [
  "Investor Name",
  "Document Type",
  "Entity Name",
  "Document Name",
  "Document Date",
];

const buildDefaultCustomizeRows = () =>
  DEFAULT_CUSTOMIZE_LABELS.map((label, i) => ({
    columnName: label,
    isVisible: true,
    order: i,
  }));

const toInputDate = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toToolbarOptions = (items = [], valueKeyCamel, valueKeyPascal, labelKeyCamel, labelKeyPascal) =>
  items.map((item) => ({
    value: get(item, valueKeyCamel, valueKeyPascal),
    label: get(item, labelKeyCamel, labelKeyPascal),
  }));

const normalizeAll = (v) => (Number(v) === -1 ? null : Number(v));

const mapLabelToField = (name) => {
  const n = (name || "").trim().toLowerCase();
  if (n === "investor name") return "InvestorName";
  if (n === "document type") return "DocumentTypeName";
  if (n === "entity" || n === "entity name") return "EntityName";
  if (n === "document name") return "DocumentName";
  if (n === "date" || n === "document date") return "DocumentDate";
  return null;
};

const applyClientFilters = (list, fFromDate, fToDate, fDescription, fCategoryIds) => {
  const from = fFromDate ? new Date(`${fFromDate}T00:00:00`) : null;
  const to = fToDate ? new Date(`${fToDate}T23:59:59`) : null;
  const q = (fDescription || "").trim().toLowerCase();

  return (list || []).filter((r) => {
    const name = (get(r, "documentName", "DocumentName") || "").toLowerCase();
    const dtRaw = get(r, "documentDate", "DocumentDate");
    const dt = dtRaw ? new Date(dtRaw) : null;
    const docType = get(r, "documentTypeId", "DocumentTypeId");

    const docTypeOk = fCategoryIds.filter(c => docType == c).length > 0;
    const dateOk = (!from || (dt && dt >= from)) && (!to || (dt && dt <= to));
    const nameOk = !q || name.includes(q);
    return dateOk && nameOk && docTypeOk;
  });
};

const getAlignmentStyle = (params) => {
  const value = params?.value;

  // actual numbers
  if (typeof value === "number") {
    return { textAlign: "right" };
  }

  // numeric strings like:
  // "25%", "-10.5", "1,245.88", etc.
  if (typeof value === "string") {
    const numericValue = Number(
      value
        .replace("%", "")
        .replace(/,/g, "")
        .trim()
    );

    const isValidNumber = !isNaN(numericValue);

    if (isValidNumber) {
      return { textAlign: "right" };
    }

    return { textAlign: "left" };
  }

  return null;
};

const AssetDocuments = () => {
  useDocumentTitle("Documents");

  const { contactId: contactIdParam } = useParams();
  const contactId = Number(atob(contactIdParam));
  const didInitRef = useRef(false);
  const categoryRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const user = useAuthStore((s) => s) || {};
  const userId = get(user, "user", "UserId");
  const appRoleId = get(user, "role", "AppRoleId");

  const [isLoading, setIsLoading] = useState(true);

  const [investors, setInvestors] = useState([]);
  const [entities, setEntities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);

  const [selectedInvestor, setSelectedInvestor] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return toInputDate(new Date(now.getFullYear() - 1, 0, 31));
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return toInputDate(new Date(now.getFullYear(), now.getMonth() + 2, 1));
  });

  const [documentName, setDocumentName] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [categoryOpen, setCategoryOpen] = useState(false);

  const [pageSize] = useState(15);

  const [showCustomize, setShowCustomize] = useState(false);
  const [customizeRows, setCustomizeRows] = useState(buildDefaultCustomizeRows());
  const [gridQueryId, setGridQueryId] = useState(0);
  const [visibleCols, setVisibleCols] = useState(new Set());

  const selectedEntityObj = useMemo(
    () => entities.find((e) => String(get(e, "entityId", "EntityId")) === String(selectedEntity)),
    [entities, selectedEntity]
  );
  const investorOptions = useMemo(
    () => toToolbarOptions(investors, "investorId", "InvestorId", "investorName", "InvestorName"),
    [investors]
  );
  const entityOptions = useMemo(
    () => toToolbarOptions(entities, "entityId", "EntityId", "entityName", "EntityName"),
    [entities]
  );

  const entityTypeId = Number(get(selectedEntityObj || {}, "entityTypeId", "EntityTypeId") || 0);

  const categorySummary =
    selectedCategoryIds.length > 0 ? `${selectedCategoryIds.length} item(s) selected` : "Select category";

  const getCustomizeUserName = () => {
    const first =
      get(user, "inputUserName", "displayUserName") ||
      "User";
    return `Default_${first}`;
  };

  const applyVisibleFromCustomizeRows = (rowsToApply) => {
    const nextVisible = new Set();
    rowsToApply.forEach((r) => {
      if (r.isVisible) {
        const f = mapLabelToField(r.columnName);
        if (f) nextVisible.add(f);
      }
    });
    setVisibleCols(nextVisible);
  };

  const loadSavedCustomize = async () => {
    try {
      const res = await getDocumentsCustomize(1, userId || 0, getCustomizeUserName(), false);
      const dto = res?.data || {};
      const cols = get(dto, "columnsSettings", "ColumnsSettings") || [];
      const qid = Number(get(dto, "gridQueryID", "GridQueryID") || 0);

      setGridQueryId(qid);

      const normalizedRows = (cols.length ? cols : buildDefaultCustomizeRows())
        .map((r, i) => ({
          columnName: get(r, "columnName", "ColumnName") ?? r.columnName,
          isVisible: !!(get(r, "isVisible", "IsVisible") ?? r.isVisible),
          order: Number(get(r, "sortOrder", "SortOrder") ?? get(r, "order", "Order") ?? r.order ?? i),
        }))
        .sort((a, b) => a.order - b.order);

      setCustomizeRows(normalizedRows);
      applyVisibleFromCustomizeRows(normalizedRows);
    } catch (e) {
      console.error("loadSavedCustomize failed", e);
      const fallback = buildDefaultCustomizeRows();
      setCustomizeRows(fallback);
      setVisibleCols(
        new Set(["InvestorName", "DocumentTypeName", "EntityName", "DocumentName", "DocumentDate"])
      );
    }
  };

  const fetchGrid = async (override = {}) => {
    setIsLoading(true);

    const investorVal = override.investorId ?? selectedInvestor;
    const entityVal = override.entityId ?? selectedEntity;
    const entityTypeVal = override.entityTypeId ?? entityTypeId;
    const selectedCats = override.categoryIds ?? selectedCategoryIds;

    if (investorVal === "" || entityVal === "") {
      setIsLoading(false);
      return;
    }

    const invId = normalizeAll(investorVal);
    const entId = normalizeAll(entityVal);
    const eType = Number(entityVal) === -1 ? null : Number(entityTypeVal || 0);

    const allCategoryIds =
      override.allCategoryIds ?? categories.map((x) => String(getDocTypeId(x))).filter(Boolean);

    if (!allCategoryIds.length || !selectedCats.length) {
      setRows([]);
      setTotalRecords(0);
      setFilteredRecords(0);
      setSelectedDocIds(new Set());
      setIsLoading(false);
      return;
    }

    try {
      const res = await getDocumentsByEntity({
        investorId: invId,
        entityId: entId,
        entityTypeId: eType,
        fromDate: "",
        toDate: "",
        documentName: "",
        categoryType: selectedCategoryIds.length > 0 ? selectedCategoryIds.join(",") : allCategoryIds.join(","),
        isCategoryExists: true,
        contactId,
        userId,
        appRoleId,
        documentGroup: 0,
      });

      const data = res?.data || {};
      const fullList = Array.isArray(data) ? data : data.data || [];

      const filteredList = applyClientFilters(
        fullList,
        override.fromDate ?? fromDate,
        override.toDate ?? toDate,
        override.documentName ?? documentName,
        override.categoryIds ?? selectedCategoryIds
      );

      setRows(filteredList);
      setTotalRecords(fullList.length);
      setFilteredRecords(filteredList.length);
      setSelectedDocIds(new Set());
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async (investorId, entityId, eTypeId) => {
    const normalizedInvestorId = normalizeAll(investorId);
    const normalizedEntityId = normalizeAll(entityId);
    const normalizedEntityTypeId = Number(entityId) === -1 ? null : Number(eTypeId || 0);

    const catRes = await getCategoryByEntity(
      normalizedInvestorId,
      normalizedEntityId,
      normalizedEntityTypeId,
      contactId
    );

    const list = catRes?.data || [];
    setCategories(list);

    const allIds = list.map((x) => String(getDocTypeId(x))).filter(Boolean);
    setSelectedCategoryIds(allIds);

    await fetchGrid({
      investorId,
      entityId,
      entityTypeId: eTypeId,
      categoryIds: allIds,
      allCategoryIds: allIds,
    });
  };

  const loadEntities = async (investorId) => {
    const normalizedInvestorId = normalizeAll(investorId);
    const entRes = await getEntitiesByInvestor(normalizedInvestorId, contactId);
    const raw = entRes?.data || [];

    const allOption = {
      entityId: -1,
      entityName: "--All--",
      entityTypeId: null,
      EntityId: -1,
      EntityName: "--All--",
      EntityTypeId: null,
    };

    const list = raw.length > 1 ? [allOption, ...raw] : raw;
    setEntities(list);

    if (!list.length) {
      setSelectedEntity("");
      setCategories([]);
      setRows([]);
      setTotalRecords(0);
      setFilteredRecords(0);
      setIsLoading(false);
      return;
    }

    if (list.length === 1) {
      const onlyId = get(list[0], "entityId", "EntityId");
      setSelectedEntity(String(onlyId));
      await loadCategories(investorId, onlyId, get(list[0], "entityTypeId", "EntityTypeId"));
    } else {
      setSelectedEntity("-1");
      await loadCategories(investorId, -1, null);
    }
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (!contactId || Number.isNaN(contactId)) return;

    const init = async () => {
      setIsLoading(true);

      const invRes = await getInvestorsByContact(contactId);
      const raw = invRes?.data || [];

      const allInvestor = {
        investorId: -1,
        investorName: "--All--",
        InvestorId: -1,
        InvestorName: "--All--",
      };

      const list = raw.length > 1 ? [allInvestor, ...raw] : raw;
      setInvestors(list);

      if (!list.length) {
        setIsLoading(false);
        return;
      }

      if (list.length === 1) {
        const onlyId = get(list[0], "investorId", "InvestorId");
        setSelectedInvestor(String(onlyId));
        await loadEntities(onlyId);
      } else {
        setSelectedInvestor("-1");
        await loadEntities(-1);
      }
    };

    init();
  }, [contactId]);

  useEffect(() => {
    if (!user) return;
    loadSavedCustomize();
  }, [user]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (categoryOpen && categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [categoryOpen]);

  const onInvestorChange = async (e) => {
    const investorId = e.target.value;
    setIsLoading(true);
    setSelectedInvestor(investorId);
    await loadEntities(investorId);
  };

  const onEntityChange = async (e) => {
    const entityId = e.target.value;
    setSelectedEntity(entityId);

    const entity = entities.find((x) => String(get(x, "entityId", "EntityId")) === String(entityId));
    if (!entity) return;

    await loadCategories(selectedInvestor, entityId, get(entity, "entityTypeId", "EntityTypeId"));
  };

  const onCategoryToggle = (id) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const onSelectAllCategories = () => {
    const allIds = categories.map((x) => String(getDocTypeId(x))).filter(Boolean);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedCategoryIds.includes(id));
    setSelectedCategoryIds(allSelected ? [] : allIds);
  };

  const toggleDoc = (docId) => {
    const key = String(docId);
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const downloadSingleDoc = async (row, openInlinePdf = false) => {
    const docId = get(row, "documentId", "DocumentId");
    const docName = get(row, "documentName", "DocumentName") || `Document_${docId}.pdf`;
    const extension = (docName.split(".").pop() || "").toLowerCase();

    const res = await downloadDocumentBlob(docId, false, userId, appRoleId);
    const blob = res?.data;
    if (!blob) return;

    if (openInlinePdf && extension === "pdf") {
      openBlobInNewTab(blob);
      return;
    }

    saveBlob(blob, docName);
  };

  const downloadRow = async (row) => {
    const docName = get(row, "documentName", "DocumentName") || "";
    const extension = (docName.split(".").pop() || "").toLowerCase();
    await downloadSingleDoc(row, extension === "pdf");
  };

  const onDownloadSelectedPdf = async () => {
    const selectedRows = rows.filter((r) =>
      selectedDocIds.has(String(get(r, "documentId", "DocumentId")))
    );

    if (!selectedRows.length) {
      toast.error("Please select at least one item to download.");
      return;
    }

    const pdfRows = selectedRows.filter((r) => {
      const nm = get(r, "documentName", "DocumentName") || "";
      return (nm.split(".").pop() || "").toLowerCase() === "pdf";
    });

    if (!pdfRows.length) {
      toast.error("Please select at least one PDF to download.");
      return;
    }

    for (const row of pdfRows) {
      await downloadSingleDoc(row, false);
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  };

  const formatZipFileName = (name) =>
    String(name || "Documents").replace(/[<>:"/\\|?*]+/g, "").trim();

  const formatZipTimestamp = (d = new Date()) => {
    const MM = String(d.getMonth() + 1).padStart(2, "0");
    const DD = String(d.getDate()).padStart(2, "0");
    const YYYY = d.getFullYear();
    const H = String(d.getHours()); 
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${MM}${DD}${YYYY}${H}${mm}${ss}`;
  };

  const openPreviewPage = async (row) => {
    const docId = get(row, "documentId", "DocumentId");
    const docName = get(row, "documentName", "DocumentName") || "";
    const extension = (docName.split(".").pop() || "").toLowerCase();

    if (extension !== "pdf") {
      downloadRow(row);
      return;
    }

    try {
      const res = await downloadDocumentBlob(docId, false, userId, appRoleId);
      const blob = res?.data;

      if (!blob) return;  
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error("Preview failed", e);
    }
  };

  const onDownloadZip = async () => {
    const ids = rows
      .filter((r) => selectedDocIds.has(String(get(r, "documentId", "DocumentId"))))
      .map((r) => get(r, "documentId", "DocumentId"));

    if (!ids.length) {
      toast.error("Please select at least one item to download.");
      return;
    }

    const res = await downloadDocumentsZipBlob(ids, userId, appRoleId, "Documents", false);
    const blob = res?.data;
    if (!blob) return;

    const investorObj = investors.find(
      (inv) =>
        String(get(inv, "investorId", "InvestorId")) === String(selectedInvestor)
    );

    const contactName = formatZipFileName(
      get(investorObj, "investorName", "InvestorName")
    );

    const fileName = `${contactName}_${formatZipTimestamp()}.zip`;

    saveBlob(blob, fileName);
  };

  const onGo = async () => {
    await fetchGrid();
    setCategoryOpen(false);
  };

  const onExportExcel = () => {
    const headers = ["Investor Name", "Document Type", "Entity", "Document Name", "Date"];

    const exportRows = rows.map((r) => ({
      "Investor Name": get(r, "investorName", "InvestorName") || "",
      "Document Type": get(r, "documentTypeName", "DocumentTypeName") || "",
      Entity: get(r, "entityName", "EntityName") || "",
      "Document Name": get(r, "documentName", "DocumentName") || "",
      Date: get(r, "documentDate", "DocumentDate")
        ? new Date(get(r, "documentDate", "DocumentDate")).toLocaleDateString("en-US")
        : "",
    }));

    exportToExcel({
      fileName: "Documents.xlsx",
      headers,
      rows: exportRows,
    });
  };

  const onOpenCustomize = async () => {
    await loadSavedCustomize();
    setShowCustomize(true);
  };

  const onSaveCustomize = async () => {
    const effectiveQid = gridQueryId || 1234;

    const payload = {
      ColumnsSettings: customizeRows.map((r, i) => ({
        ColumnName: r.columnName,
        IsVisible: !!r.isVisible,
        SortOrder: i,
        Order: i,
        GroupIndex: effectiveQid,
      })),
      GridID: 123,
      GridQueryID: effectiveQid,
      IsDefault: true,
      IsShared: true,
      PageID: 1,
      QueryName: getCustomizeUserName(),
      UserID: userId || 0,
    };

    try {
      await saveGridCustomize(payload);
      await loadSavedCustomize();
      setShowCustomize(false);
    } catch (e) {
      console.error("onSaveCustomize failed", e);
    }
  };

  const customizeColumnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "isVisible",
        width: 90,
        headerComponent: () => {
          const allChecked =
            customizeRows.length > 0 &&
            customizeRows.every((r) => r.isVisible);

          return (
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(e) => {
                const checked = e.target.checked;

                setCustomizeRows((prev) =>
                  prev.map((row) => ({
                    ...row,
                    isVisible: checked,
                  }))
                );
              }}
            />
          );
        },
        cellRenderer: (params) => (
          <input
            type="checkbox"
            checked={params.value}
            onChange={(e) => {
              const checked = e.target.checked;

              setCustomizeRows((prev) =>
                prev.map((row, i) =>
                  i === params.node.rowIndex
                    ? { ...row, isVisible: checked }
                    : row
                )
              );
            }}
          />
        ),
        cellStyle:getAlignmentStyle
      },
      {
        headerName: "Column Name",
        field: "columnName",
        flex: 1,
        cellStyle:getAlignmentStyle
      },
    ],
    [customizeRows]
  );

  const onCustomizeRowDragEnd = (event) => {
    const updatedRows = [];
    event.api.forEachNode((node) => updatedRows.push(node.data));
    setCustomizeRows(updatedRows.map((r, i) => ({ ...r, order: i })));
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      suppressHeaderFilterButton: true
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Investor Name",
        cellStyle:getAlignmentStyle,
        field: "InvestorName",
        flex: 1.2,
        minWidth: 180,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "investorName", "InvestorName") || "",
      },
      {
        headerName: "Document Type",
        cellStyle:getAlignmentStyle,
        field: "DocumentTypeName",
        flex: 1.1,
        minWidth: 170,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "documentTypeName", "DocumentTypeName") || "",
      },
      {
        headerName: "Entity",
        cellStyle:getAlignmentStyle,
        field: "EntityName",
        flex: 1.1,
        minWidth: 170,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "entityName", "EntityName") || "",
      },
      {
        headerName: "Document Name",
        cellStyle:getAlignmentStyle,
        field: "DocumentName",
        flex: 1.6,
        minWidth: 250,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "documentName", "DocumentName") || "",
        cellRenderer: (p) => {
          const name = p.value || "";
          const isNew = !!get(p.data, "isNew", "IsNew");
          const viewed = !!get(p.data, "viewed", "Viewed");
          return (
            <span>
              {name}
              {isNew && !viewed ? (
                <img src={newIcon} alt="new" style={{ marginLeft: 6, width: 28, height: 18 , cursor:'pointer' }} />
              ) : null}
            </span>
          );
        },
      },
      {
        headerName: "Date",
        field: "DocumentDate",
        cellStyle:getAlignmentStyle,
        flex: 0.9,
        minWidth: 130,
        filter: "agTextColumnFilter",
        valueGetter: (p) => get(p.data, "documentDate", "DocumentDate"),
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString("en-US") : ""),
      },
      {
        headerName: "Select",
        cellStyle:getAlignmentStyle,
        field: "Select",
        width: 90,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => {
          const docId = get(params.data, "documentId", "DocumentId");
          const key = String(docId);
          return (
            <input
              type="checkbox"
              checked={selectedDocIds.has(key)}
              onChange={() => toggleDoc(docId)}
            />
          );
        },
      },
    ],
    [selectedDocIds]
  );

  const visibleColumnDefs = useMemo(() => {
    if (!visibleCols.size) return columnDefs;
    return columnDefs.filter((c) => c.field === "Select" || visibleCols.has(c.field));
  }, [columnDefs, visibleCols]);

  return (
    <div className="minHeightDiv1">
      <section className="col-md-12 action-bar">
        <form className="statement-filter-form" onSubmit={(e) => e.preventDefault()}>
          <div className="row statement-filter-row toolbar">
              <div className="col-sm-4 col-md-3 statement-filter-item statement-filter-item-top">
                <label className="control-label custom-fonts-label filter-lbl" style={{ margin: 0 }}>
                  Investor:
                </label>
                <ToolbarSelect
                  value={selectedInvestor}
                  onChange={(nextValue) => onInvestorChange({ target: { value: nextValue } })}
                  options={investorOptions}
                />
              </div>

              <div className="col-sm-4 col-md-3 statement-filter-item statement-filter-item-top">
                <label className="control-label custom-fonts-label filter-lbl" style={{ margin: 0 }}>
                  Entity:
                </label>
                <ToolbarSelect
                  value={selectedEntity}
                  onChange={(nextValue) => onEntityChange({ target: { value: nextValue } })}
                  options={entityOptions}
                />
              </div>

              <div
                className="col-sm-4 col-md-3 statement-filter-item statement-filter-item-top"
                style={{position: "relative", padding:0}}
              >
                <label className="" style={{ margin: 0 }}>
                  From:
                </label>
                <input
                  id="fromDate"
                  name="fromDate"
                  className="form-control custom-fonts"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ width:"90%"}}
                />
              </div>

              <div
                className="col-sm-4 col-md-3 statement-filter-item statement-filter-item-top"
                style={{position: "relative", padding:0}}
              >
                <label className="" style={{ margin: 0 }}>
                  To:
                </label>
                <input
                  id="toDate"
                  name="toDate"
                  className="form-control custom-fonts"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{width:"90%"}}
                />
              </div>



              <div
                ref={categoryRef}
                className="col-sm-4 col-md-4 statement-filter-item statement-filter-item-bottom"
                style={{ position: "relative" }}
              >
                <label className="control-label custom-fonts-label filter-lbl" style={{ marginTop: 0 }}>
                  Category:
                </label>
                <button
                  type="button"
                  className="form-control custom-fonts text-left category-dropdown-btn"
                  onClick={() => setCategoryOpen((v) => !v)}
                >
                  <span>{categorySummary}</span>
                  <span className={`category-chevron ${categoryOpen ? "open" : ""}`}>
                    <img src={dropDownImg} alt="" style={{height:"40px", marginTop:"-5px" }} />
                  </span>
                </button>

                {categoryOpen && (
                  <div
                    className="category-dropdown-menu"
                  >
                    <label className="category-dropdown-option category-dropdown-option-all">
                      <input
                        type="checkbox"
                        checked={categories.length > 0 && categories.map((x) => String(getDocTypeId(x))).filter(Boolean).every((id) => selectedCategoryIds.includes(id))}
                        onChange={onSelectAllCategories}
                        style={{ marginRight: 8 }}
                      />
                      <span className="category-dropdown-option-text">Select All</span>
                    </label>

                    {categories.map((c) => {
                      const id = String(getDocTypeId(c));
                      const label = c?.documentTypeName ?? c?.DocumentTypeName ?? "";
                      return (
                        <label key={id} className="category-dropdown-option">
                          <input
                            type="checkbox"
                            checked={selectedCategoryIds.includes(id)}
                            onChange={() => onCategoryToggle(id)}
                            style={{ marginRight: 8 }}
                          />
                          <span className="category-dropdown-option-text">{label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="col-sm-4 col-md-4 statement-filter-item statement-filter-item-bottom">
                <label className="control-label custom-fonts-label filter-lbl" style={{ margin: 0 }}>
                  Name:
                </label>
                <input
                  className="form-control custom-fonts"
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onGo()}
                />
              </div>

              
            <div className="col-sm-4 col-md-4 statement-filter-item statement-filter-item-bottom statement-go-col">
              <button type="button" className="btn btn2 focus-remove custom-fonts go-btn" style={{ marginTop: 0 }} onClick={onGo}>
                Go
              </button>
            </div>

          </div>
        </form>
      </section>

      <section className="col-md-12 asset-content-ov">
        <div className="statement-toolbar">
          <div className="statement-toolbar-right">
            <div className="statement-counts">
              <span>Total : {totalRecords}</span>
              <span>|</span>
              <span>Filtered : {filteredRecords}</span>
            </div>

            <button type="button" className="icon-btn" title="Customize" onClick={onOpenCustomize}>
              <img src={settingsIcon} alt="Customize" />
            </button>
            <button type="button" className="icon-btn" title="Export to Excel" onClick={onExportExcel}>
              <img src={excelIcon} alt="Excel" />
            </button>
            <button type="button" className="icon-btn" title="Download PDF" onClick={onDownloadSelectedPdf}>
              <img src={pdfIcon} alt="PDF" />
            </button>
            <button type="button" className="icon-btn" title="Download As Zip" onClick={onDownloadZip}>
              <img src={downloadIcon} alt="Zip" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="ag-theme-alpine" style={{ width: "100%" }}>
            <AgGridReact
              theme={"legacy"}
              rowData={rows}
              columnDefs={visibleColumnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              getRowStyle={(params) => {
            if (params.data?.isTotalPortfolio) {
              return {
                backgroundColor: "#E6E3F0",
                fontWeight: "bold"
              };
            }

            return {
              backgroundColor: "#FFFFFF"
            };
          }}

              onRowDoubleClicked={(e) => openPreviewPage(e.data)}
              pagination={true}
              paginationPageSize={pageSize}
              paginationPageSizeSelector={[10, 15, 50, 100, 200]}
            />
          </div>
        )}

        {!isLoading && !rows.length && (
          <div className="text-center" style={{ marginTop: 12 }}>
            <b>No Records Available</b>
          </div>
        )}
      </section>

      <AESClientWebsiteDrawer
        open={showCustomize}
        setOpen={setShowCustomize}
        title="Customize View Settings"
        size="sm"
      >
        <div style={{ height: 350 }} className="ag-theme-alpine">
          <AgGridReact
            theme={"legacy"}
            rowData={customizeRows}
            columnDefs={customizeColumnDefs}
            rowDragManaged={true}
            animateRows={true}
            suppressMoveWhenRowDragging={true}
            domLayout="autoHeight"
            onRowDragEnd={onCustomizeRowDragEnd}
            defaultColDef={{ sortable: false, filter: false, resizable: true }}
          />
        </div>

        <div style={{ marginTop: 15, textAlign: "right" }}>
          <button className="btn btn-default" onClick={() => setShowCustomize(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" style={{ marginLeft: 10 }} onClick={onSaveCustomize}>
            Save
          </button>
        </div>
      </AESClientWebsiteDrawer>
    </div>
  );
};

export default AssetDocuments;
